"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useAuth } from "@/hooks/useAuth";
import { DAILY_MODE_EVENT, refreshNextSteps, useNavState } from "@/lib/nav-state";
import type { NextDaily } from "@/app/api/next-steps/route";
import { SHELF_RESET_EVENT, clearShelfView, loadShelfView } from "@/lib/shelf-view";
import LibraryIcon from "@/components/ui/LibraryIcon";
import { useUiTexts } from "@/lib/ui-texts-client";
import { DEFAULT_NAV_TABS, fallbackNavTab, navTabHref, ownNavTab, type NavTab } from "@/lib/nav-tabs";

/** 前に読んだナビの並びを覚えておく置き場所（開き直したときに、いまの並びが一瞬出てから入れ替わらないように） */
const NAV_TABS_CACHE = "navTabs";
const noopSubscribe = () => () => {};
function readCachedTabs(): string | null {
  try {
    return localStorage.getItem(NAV_TABS_CACHE);
  } catch {
    return null;
  }
}

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [uiIcons, setUiIcons] = useState<Record<string, string>>({});
  // 並び・名前・つながる画面は管理UIの「下のナビ」で公開したもの。未公開ならいまの並び
  const [fetchedTabs, setFetchedTabs] = useState<NavTab[] | null>(null);
  const cachedJson = useSyncExternalStore(noopSubscribe, readCachedTabs, () => null);
  const tabs = useMemo(() => {
    if (fetchedTabs) return fetchedTabs;
    try {
      const cached = JSON.parse(cachedJson ?? "null") as NavTab[] | null;
      if (Array.isArray(cached) && cached.length > 0) return cached;
    } catch {}
    return DEFAULT_NAV_TABS;
  }, [fetchedTabs, cachedJson]);
  // 自分の本を持たない画面では、来た元の本を光らせたままにする
  const own = ownNavTab(tabs, pathname);
  const [lastTab, setLastTab] = useState<string | null>(own);
  if (own && own !== lastTab) setLastTab(own);
  const activeTab = own ?? lastTab ?? fallbackNavTab(tabs, pathname);
  // 選ぶ本が変わったら、前に開いていた本を閉じる動きの間だけ覚えておく（globals.css の data-closing）
  const [openTab, setOpenTab] = useState(activeTab);
  const [closingTab, setClosingTab] = useState<string | null>(null);
  if (activeTab !== openTab) {
    setClosingTab(openTab);
    setOpenTab(activeTab);
  }
  const { readingDone, nextSteps } = useNavState();
  // 知らせの文は管理UIの「文言」で書き換えられる（src/lib/ui-texts.ts の nav.*）
  const { t } = useUiTexts();
  const { user } = useAuth();

  // 進化できる・道が開けるの点。画面を移るたびに読みに行くが、30秒以内なら読み直さない
  useEffect(() => {
    if (!user) return;
    user.getIdToken().then((token) => refreshNextSteps(token)).catch(() => {});
  }, [user, pathname]);
  /** 本に付ける点（その画面で「今やるといいこと」がある）。点はつながる画面で決まる */
  const dotOf = (tab: NavTab): string | null =>
    tab.destination === "spirit" && nextSteps?.evolution?.ready
      ? t("nav.evolution", { 名前: nextSteps.evolution.name })
      : tab.destination === "map" && nextSteps?.road?.affordable
        ? t("nav.road", { 名前: nextSteps.road.name })
        : null;

  // 今日の読み方のしおり（書庫の本に挟む）。決めた直後は合図で受け取り、差し込む演出を一度だけ出す。ふだんは next-steps から
  const [chosenDaily, setChosenDaily] = useState<NextDaily | null>(null);
  const [inserting, setInserting] = useState(false);
  useEffect(() => {
    const onChosen = (e: Event) => {
      setChosenDaily((e as CustomEvent<NextDaily>).detail);
      setInserting(true);
    };
    window.addEventListener(DAILY_MODE_EVENT, onChosen);
    return () => window.removeEventListener(DAILY_MODE_EVENT, onChosen);
  }, []);
  const daily = chosenDaily ?? nextSteps?.daily ?? null;
  // 読み方が戻る時刻を過ぎたら外す（その時刻に描き直す）
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!daily) return;
    const ms = daily.nextResetAt - Date.now();
    if (ms <= 0) return;
    const id = window.setTimeout(() => { setChosenDaily(null); setTick((n) => n + 1); }, Math.min(ms + 1000, 2_147_000_000));
    return () => window.clearTimeout(id);
  }, [daily]);
  const ribbon = daily && daily.nextResetAt > Date.now() ? daily : null;

  useEffect(() => {
    fetch("/api/nav-tabs")
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { tabs: NavTab[] | null } | null) => {
        const next = data?.tabs ?? DEFAULT_NAV_TABS;
        setFetchedTabs(next);
        try {
          localStorage.setItem(NAV_TABS_CACHE, JSON.stringify(next));
        } catch {}
      })
      .catch(() => {});
    fetch("/api/ui-icons")
      .then((r) => r.ok ? r.json() : {})
      .then((data) => setUiIcons(data))
      .catch(() => {});
  }, []);

  // 読書中は本文の場所を広く取るため隠す。読み終えたあと（読了後の画面）は出す
  if (pathname === "/login" || (pathname.startsWith("/read/") && !readingDone)) {
    return null;
  }

  return (
    <nav className="library-navigation" aria-label={t("nav.aria")}>
      <div className="library-navigation-inner">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const customIcon = tab.imageKey ? uiIcons[tab.imageKey] : undefined;
          const dot = dotOf(tab);
          // 書庫の本には、今日の読み方の色のしおりを挟む（書庫をナビから外していれば挟まない）
          const ribbonHere = ribbon && tab.destination === "shelf" ? ribbon : null;
          const note = [dot, ribbonHere && t("nav.dailyRibbon", { 読み方: ribbonHere.label })].filter(Boolean).join("・") || null;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                const href = navTabHref(tab);
                if (tab.destination === "shelf") {
                  // 書庫の中で押したら冒頭へ（覚えていた所を消す）。ほかの画面から押したら、前に見ていた書庫の画面へ
                  if (pathname === "/shelf" || pathname.startsWith("/shelf/")) {
                    clearShelfView();
                    const top = new URLSearchParams(href.split("?")[1] ?? "").get("mainTab");
                    window.dispatchEvent(new CustomEvent(SHELF_RESET_EVENT, { detail: { mainTab: top } }));
                    router.push(href);
                    return;
                  }
                  if (loadShelfView()) {
                    router.push("/shelf");
                    return;
                  }
                }
                router.push(href);
              }}
              aria-current={isActive ? "page" : undefined}
              data-leather={tab.leather}
              data-closing={!isActive && tab.id === closingTab ? "" : undefined}
              onAnimationEnd={(e) => {
                if (e.animationName === "library-book-close") setClosingTab(null);
              }}
              aria-label={note ? `${tab.label}（${note}）` : undefined}
              title={note ?? undefined}
              className="library-nav-item"
            >
              {/* 選んでいるタブの本は開いて見開きになる（globals.css の aria-current と ::after の表紙） */}
              <span className="relative">
                {customIcon ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={customIcon} alt="" className="h-6 w-6 object-contain" />
                ) : (
                  <LibraryIcon name={tab.icon} />
                )}
              </span>
              {/* 今やるといいことがある印（進化できる・道が開ける）。本の上の縁から付箋がのぞく（globals.css の .library-sticky-tab）。放っておいても大きく光らせない */}
              {dot && <span aria-hidden="true" className="library-sticky-tab" />}
              {/* 今日の読み方のしおり（選んだ付箋の色）。本の上の縁から燕尾の先がのぞく。決めた直後だけ差し込む動き（globals.css の .library-nav-ribbon） */}
              {ribbonHere && (
                <span
                  aria-hidden="true"
                  className="library-nav-ribbon"
                  data-mode={ribbonHere.mode}
                  data-inserting={inserting || undefined}
                  onAnimationEnd={() => setInserting(false)}
                />
              )}
              <span className="library-nav-label">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
